import { memo } from 'react'
import { Toast } from '@base-ui/react/toast'
import { clsx } from 'clsx'

import css from './ToastRegion.module.scss'

import { toastManager, type ToastTone } from '@/lib/toast'

import CloseIcon from '@/assets/svgs/close.svg?react'
import CircleAlertIcon from '@/assets/svgs/status/circle-alert.svg?react'
import CircleCheckIcon from '@/assets/svgs/status/circle-check.svg?react'
import CircleInfoIcon from '@/assets/svgs/status/circle-info.svg?react'

const TONE_ICONS: Record<ToastTone, typeof CircleCheckIcon> = {
  success: CircleCheckIcon,
  error: CircleAlertIcon,
  info: CircleInfoIcon
}

function isTone(value: string | undefined): value is ToastTone {
  return value === 'success' || value === 'error' || value === 'info'
}

function ToastList() {
  const { toasts } = Toast.useToastManager()

  return toasts.map((toast) => {
    const tone: ToastTone = isTone(toast.type) ? toast.type : 'info'
    const Icon = TONE_ICONS[tone]

    return (
      <Toast.Root key={toast.id} toast={toast} className={clsx(css.toast, css[tone])}>
        <span className={css.icon} aria-hidden="true">
          <Icon />
        </span>
        <Toast.Content className={css.body}>
          <Toast.Title className={css.title} />
          <Toast.Description className={css.description} />
          {toast.actionProps && <Toast.Action className={css.action} />}
        </Toast.Content>
        <Toast.Close className={css.close} aria-label="Dismiss notification">
          <CloseIcon />
        </Toast.Close>
      </Toast.Root>
    )
  })
}

/**
 * Mounts once at the app root. Bound to the module-level `toastManager` so
 * anything — including code outside React — can raise a notification.
 */
function ToastRegion() {
  return (
    <Toast.Provider toastManager={toastManager} limit={4}>
      <Toast.Portal>
        <Toast.Viewport className={css.viewport}>
          <ToastList />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  )
}

export default memo(ToastRegion)
